import UserSettingsLayout from "../../layout/UserSettingsLayout.jsx";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Pagination.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useCallback, useMemo, useState } from "react";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import { UserApi, ProjectApi } from "../../api/authority/generated/index.ts";
import UserProjectItem from "./UserProjectItem.jsx";
import { GlobalContext } from "../../utils/context/GlobalContextProvider.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/Button.jsx";
import Modal from "../../components/Modal.jsx";
import FormContainer from "../../components/form/FormContainer.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import FormButton from "../../components/form/FormButton.jsx";
import FormError from "../../components/form/FormError.jsx";
import toast from "react-hot-toast";
import { useErrorTranslation } from "../../api/utilities/translateErrorMessage.js";

// Cache to store fetched results by page
const fetchCache = new Map();

const fetchUserProjects = async (authorityConfiguration, userId, page) => {
    const cacheKey = `${userId}-${page}`;
    if (fetchCache.has(cacheKey)) {
        return fetchCache.get(cacheKey);
    }
    const api = new UserApi(authorityConfiguration);
    const result = await api.userProjects({ userId, page });
    fetchCache.set(cacheKey, result);
    return result;
};

export const DEFAULT_PROJECT_LOGO_URL = import.meta.VITE_AUTHORITY_URL + '/static/images/no-logo.png';

export default function UserProjectList() {
    const { t } = useTranslation();
    const { et } = useErrorTranslation();
    const { context, setCurrentProjectId, getAuthorityConfiguration, resetCurrentUserProjects } = useContext(GlobalContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page') ?? '1');
    const [isDownloading, setIsDownloading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [addProjectModalOpened, setAddProjectModalOpened] = useState(false);
    const [addProjectLoading, setAddProjectLoading] = useState(false);
    const [projectInput, setProjectInput] = useState({
        name: '',
        logoUrl: DEFAULT_PROJECT_LOGO_URL,
    });
    const [projectCreateError, setProjectCreateError] = useState(null);
    const authorityConfiguration = getAuthorityConfiguration();
    const navigate = useNavigate();

    // Refs to avoid redundant fetches
    const fetchRef = useRef(null);
    const prevResultRef = useRef(null);
    const hasFetchedRef = useRef(false);

    // Stable fetch function for projects
    const fetchProjects = useCallback(() => {
        if (!context.currentIdentity?.user.id) {
            if (prevResultRef.current === null) {
                setResult({ data: [], pagination: { currentPage: 1, totalPages: 1 } });
            }
            setIsDownloading(false);
            return;
        }
        const cacheKey = `${context.currentIdentity.user.id}-${page}`;
        if (fetchRef.current === cacheKey) return;

        if (!hasFetchedRef.current) {
            fetchRef.current = cacheKey;
            setIsDownloading(true);
        }
        fetchUserProjects(authorityConfiguration, context.currentIdentity.user.id, page)
            .then((res) => {
                if (JSON.stringify(res) !== JSON.stringify(prevResultRef.current)) {
                    setResult(res);
                    prevResultRef.current = res;
                }
                hasFetchedRef.current = true;
            })
            .catch((e) => setError(e))
            .finally(() => {
                fetchRef.current = null;
                setIsDownloading(false);
            });
    }, [page, context.currentIdentity?.user.id, authorityConfiguration]);

    // Sync search params if needed
    useEffect(() => {
        const currentPage = searchParams.get('page') ?? '1';
        if (parseInt(currentPage) !== page) {
            setSearchParams({ page: page.toString() }, { replace: true });
        }
    }, [page, searchParams, setSearchParams]);

    // Fetch on mount or when dependencies change
    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchProjects();
        }
    }, [fetchProjects]);

    // Handle project creation
    useEffect(() => {
        if (!addProjectLoading) return;

        new ProjectApi(authorityConfiguration)
            .createProject({
                projectInput: projectInput,
            })
            .then((res) => {
                toast.success(t('project.created'));
                resetCurrentUserProjects();
                setCurrentProjectId(res.id);
                setAddProjectModalOpened(false);
                setProjectInput({ name: '', logoUrl: DEFAULT_PROJECT_LOGO_URL });
                fetchProjects();
                navigate(`/project/${res.id}/settings`);
            })
            .catch((e) => {
                et(e).then((errorMsg) => {
                    setProjectCreateError(errorMsg);
                });
            })
            .finally(() => {
                setAddProjectLoading(false);
            });
    }, [addProjectLoading, authorityConfiguration, projectInput, t, et, resetCurrentUserProjects, setCurrentProjectId, navigate]);

    const actionButton = useMemo(
        () => (
            <Button className={'gap-2'} onClick={() => setAddProjectModalOpened(true)}>
                <FontAwesomeIcon icon={fas.faPlus} /> {t('project.create')}
            </Button>
        ),
        [t]
    );

    return (
        <UserSettingsLayout title={t('projects.projects')} actions={actionButton}>
            <LoadingComponent loading={isDownloading}>
                {result && result.data && (
                    <div className="table-wrap overflow-x-auto mb-2">
                        <table className="project-list w-full dark:text-slate-300">
                            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700 border-b border-slate-200 dark:border-slate-700">
                            {result.data.map((project) => (
                                <UserProjectItem
                                    key={project.id}
                                    project={project}
                                    onProjectIdSelected={setCurrentProjectId}
                                />
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination
                    pagination={result?.pagination}
                    onPageChange={(newPage) => {
                        setSearchParams({ page: newPage.toString() });
                        hasFetchedRef.current = false;
                    }}
                />
            </LoadingComponent>

            {/* Add project modal */}
            <Modal
                className={'w-full lg:w-11/12 max-w-lg'}
                visible={addProjectModalOpened}
                title={t('project.create')}
                setVisible={setAddProjectModalOpened}
                loading={addProjectLoading}
            >
                <FormContainer>
                    {projectCreateError && <FormError error={projectCreateError} />}
                    <FormControl
                        label={t('project.name')}
                        type={'projectName'}
                        value={projectInput.name}
                        onChange={(e) =>
                            setProjectInput({
                                ...projectInput,
                                name: e.target.value,
                            })
                        }
                    />
                    <div className="flex items-center justify-end mt-6">
                        <Button onClick={() => setAddProjectLoading(true)}>
                            {t('project.create')}
                        </Button>
                    </div>
                </FormContainer>
            </Modal>
        </UserSettingsLayout>
    );
}